import { useCallback, useEffect, useRef, useState } from 'react';

import type { ErrorKind, RecorderState } from '../state/types';

interface AudioRecording {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

interface UseAudioRecorderOptions {
  onComplete: (rec: AudioRecording) => void | Promise<void>;
  onError?: (kind: ErrorKind, cause?: unknown) => void;
}

interface UseAudioRecorderResult {
  state: RecorderState;
  durationMs: number;
  error: ErrorKind | null;
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
}

const MIME_PREFERENCES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
] as const;

function pickSupportedMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of MIME_PREFERENCES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return null;
}

const MIN_RECORDING_MS = 200;

export function useAudioRecorder({
  onComplete,
  onError,
}: UseAudioRecorderOptions): UseAudioRecorderResult {
  const [state, setState] = useState<RecorderState>('idle');
  const [durationMs, setDurationMs] = useState(0);
  const [error, setError] = useState<ErrorKind | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, []);

  function cleanup() {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => {
      t.stop();
    });
    streamRef.current = null;
    recorderRef.current = null;
    chunksRef.current = [];
  }

  function fail(kind: ErrorKind, cause?: unknown) {
    if (!mountedRef.current) return;
    cleanup();
    setError(kind);
    setState('idle');
    setDurationMs(0);
    onError?.(kind, cause);
  }

  const start = useCallback(async () => {
    if (state === 'recording' || state === 'requesting') return;
    setError(null);
    setState('requesting');

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      fail('NotSupported');
      return;
    }
    const mime = pickSupportedMime();
    if (!mime) {
      fail('NotSupported');
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (cause) {
      const name = (cause as { name?: string } | null)?.name;
      const kind: ErrorKind =
        name === 'NotAllowedError' || name === 'SecurityError'
          ? 'NotAllowed'
          : name === 'NotFoundError' || name === 'OverconstrainedError'
            ? 'NotFound'
            : 'NotSupported';
      fail(kind, cause);
      return;
    }

    if (!mountedRef.current) {
      stream.getTracks().forEach((t) => {
        t.stop();
      });
      return;
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: mime });
    } catch (cause) {
      stream.getTracks().forEach((t) => {
        t.stop();
      });
      fail('NotSupported', cause);
      return;
    }

    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onerror = (e) => {
      fail('Aborted', e);
    };
    recorder.onstop = () => {
      const finishedAt = performance.now();
      const recordedMs = Math.max(0, Math.round(finishedAt - startedAtRef.current));
      const blob = new Blob(chunksRef.current, { type: mime });
      cleanup();
      if (!mountedRef.current) return;

      if (recordedMs < MIN_RECORDING_MS || blob.size === 0) {
        fail('Aborted');
        return;
      }

      setState('uploading');
      setDurationMs(recordedMs);
      void Promise.resolve(onComplete({ blob, mimeType: mime, durationMs: recordedMs })).finally(
        () => {
          if (!mountedRef.current) return;
          setState('idle');
          setDurationMs(0);
        },
      );
    };

    streamRef.current = stream;
    recorderRef.current = recorder;
    startedAtRef.current = performance.now();
    recorder.start();
    setState('recording');
    setDurationMs(0);

    tickRef.current = window.setInterval(() => {
      setDurationMs(Math.max(0, Math.round(performance.now() - startedAtRef.current)));
    }, 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, onComplete, onError]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  }, []);

  const reset = useCallback(() => {
    cleanup();
    setError(null);
    setState('idle');
    setDurationMs(0);
  }, []);

  return { state, durationMs, error, start, stop, reset };
}
